import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Plus, X, RotateCcw } from "lucide-react";
import { PROFILE as DEFAULTS } from "../data/profile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Field schemas for list-of-object sections
const SCHEMAS = {
  awards: [
    { key: "year", label: "Year", placeholder: "2025" },
    { key: "title", label: "Title", placeholder: "AVCA Phenom" },
    { key: "note", label: "Note (optional)", placeholder: "Westminster Wildcats" },
  ],
  upcoming: [
    { key: "name", label: "Tournament", placeholder: "Southern Exposure" },
    { key: "start", label: "Start", placeholder: "May 30, 2026" },
    { key: "end", label: "End", placeholder: "May 31, 2026" },
    { key: "venue", label: "Venue", placeholder: "Cobb Galleria" },
    { key: "city", label: "City", placeholder: "Atlanta, GA" },
  ],
  press: [
    { key: "outlet", label: "Outlet", placeholder: "PrepDig" },
    { key: "date", label: "Date", placeholder: "Apr 2026" },
    { key: "title", label: "Title", placeholder: "Article title" },
    { key: "url", label: "URL", placeholder: "https://..." },
  ],
  profiles: [
    { key: "label", label: "Label", placeholder: "Hudl — Game Film" },
    { key: "url", label: "URL", placeholder: "https://..." },
  ],
};

const SECTIONS = [
  { id: "vitals", title: "Vitals & IDs", type: "fields", fields: [
    { key: "gpa", label: "GPA", placeholder: "3.7" },
    { key: "act", label: "ACT", placeholder: "31" },
    { key: "major", label: "Intended Major", placeholder: "Psychology" },
    { key: "height", label: "Height", placeholder: "6'1\"" },
    { key: "weight", label: "Weight", placeholder: "150 lbs" },
    { key: "classYear", label: "Class Year", placeholder: "2027", numeric: true },
    { key: "schoolJersey", label: "School Jersey #", placeholder: "6", numeric: true },
    { key: "clubJersey", label: "Club Jersey #", placeholder: "31", numeric: true },
    { key: "usavId", label: "USAV ID", placeholder: "4392039" },
    { key: "aauId", label: "AAU ID", placeholder: "W54CD5A5" },
  ]},
  { id: "programs", title: "Club & School", type: "fields", fields: [
    { key: "school", label: "High School", placeholder: "The Westminster Schools" },
    { key: "schoolLocation", label: "School Location", placeholder: "Atlanta, GA" },
    { key: "club", label: "Club", placeholder: "A5 Volleyball Club" },
    { key: "clubTeam", label: "Club Team", placeholder: "17-2 Angel" },
    { key: "clubLocation", label: "Club Location", placeholder: "Roswell, GA" },
  ]},
  { id: "reel", title: "Highlight Reel", type: "fields", fields: [
    { key: "reelUrl", label: "Video URL (YouTube, Hudl, or Vimeo)", placeholder: "https://www.youtube.com/watch?v=…" },
  ]},
  { id: "contacts", title: "Coach References", type: "nested", groups: [
    { key: "email", label: "Faith's Direct Email", placeholder: "faithahyisena.volleyball@gmail.com" },
    { key: "schoolCoach", label: "High School Coach", fields: [
      { key: "name", label: "Name", placeholder: "Jaime Maxey" },
      { key: "email", label: "Email", placeholder: "jaimemaxey@westminster.net" },
      { key: "title", label: "Title", placeholder: "Westminster Head Coach" },
    ]},
    { key: "clubCoach", label: "Club Coach", fields: [
      { key: "name", label: "Name", placeholder: "Angel Gonzalez" },
      { key: "email", label: "Email", placeholder: "agescobales@gmail.com" },
      { key: "title", label: "Title", placeholder: "A5 17-2 Head Coach" },
    ]},
  ]},
  { id: "bio", title: "Personal Bio", type: "longtext" },
  { id: "apClasses", title: "AP Classes Taken", type: "strings" },
  { id: "extracurriculars", title: "Extracurricular Activities", type: "strings" },
  { id: "awards", title: "Honors & Accolades", type: "objects" },
  { id: "tournaments", title: "My Past Tournaments", type: "strings" },
  { id: "upcoming", title: "My Upcoming Tournaments", type: "objects" },
  { id: "press", title: "Press & Mentions", type: "objects" },
  { id: "profiles", title: "Profile Links", type: "objects" },
];

const SCALAR_KEYS = [
  "gpa", "act", "major", "height", "weight", "classYear",
  "schoolJersey", "clubJersey", "usavId", "aauId",
  "school", "schoolLocation", "club", "clubTeam", "clubLocation",
  "email", "reelUrl",
];
const COACH_KEYS = ["schoolCoach", "clubCoach"];

export default function ContentEditor({ token }) {
  const [state, setState] = useState(buildInitial({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/content`);
        setState(buildInitial(data || {}));
      } catch {
        toast.error("Could not load saved content.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        bio: state.bio,
        apClasses: state.apClasses.filter((s) => s.trim()),
        extracurriculars: state.extracurriculars.filter((s) => s.trim()),
        tournaments: state.tournaments.filter((s) => s.trim()),
        awards: state.awards.filter((o) => o.title?.trim()),
        upcoming: state.upcoming.filter((o) => o.name?.trim()),
        press: state.press.filter((o) => o.title?.trim() && o.url?.trim()),
        profiles: state.profiles.filter((o) => o.label?.trim() && o.url?.trim()),
      };
      // Scalar fields: only include if non-empty (so blanks fall back to defaults)
      for (const k of SCALAR_KEYS) {
        const v = state[k];
        if (v !== undefined && v !== null && String(v).trim() !== "") {
          payload[k] = ["classYear", "schoolJersey", "clubJersey"].includes(k)
            ? Number(v) || v
            : v;
        }
      }
      // Coach objects: include if at least name is set
      for (const k of COACH_KEYS) {
        const v = state[k];
        if (v && v.name?.trim()) payload[k] = v;
      }
      await axios.put(`${API}/admin/content`, payload, {
        headers: { "X-Admin-Password": token },
      });
      toast.success("Content saved. Reload the landing page to see changes.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const resetSection = (id) => {
    if (!window.confirm(`Reset "${id}" to defaults? (Unsaved edits in this section are lost.)`)) return;
    if (id === "vitals") {
      setState((s) => {
        const next = { ...s };
        ["gpa", "act", "major", "height", "weight", "classYear", "schoolJersey", "clubJersey", "usavId", "aauId"].forEach((k) => { next[k] = DEFAULTS[k] ?? ""; });
        return next;
      });
      return;
    }
    if (id === "programs") {
      setState((s) => {
        const next = { ...s };
        ["school", "schoolLocation", "club", "clubTeam", "clubLocation"].forEach((k) => { next[k] = DEFAULTS[k] ?? ""; });
        return next;
      });
      return;
    }
    if (id === "reel") {
      setState((s) => ({ ...s, reelUrl: DEFAULTS.reelUrl ?? "" }));
      return;
    }
    if (id === "contacts") {
      setState((s) => ({
        ...s,
        email: DEFAULTS.email ?? "",
        schoolCoach: { ...(DEFAULTS.schoolCoach || {}) },
        clubCoach: { ...(DEFAULTS.clubCoach || {}) },
      }));
      return;
    }
    setState((s) => ({ ...s, [id]: cloneDefault(id) }));
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-[#8A8A8A]">
        <Loader2 size={16} className="animate-spin" /> Loading content…
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="content-editor">
      {/* Sidebar */}
      <aside className="lg:col-span-3 border border-white/10 p-2 h-fit lg:sticky lg:top-24">
        <ul className="flex lg:flex-col overflow-x-auto lg:overflow-visible">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <button
                onClick={() => setActiveId(s.id)}
                className={`w-full text-left px-4 py-3 text-[11px] tracking-[0.22em] uppercase font-bold transition-colors whitespace-nowrap ${
                  activeId === s.id
                    ? "bg-[#E60000] text-white"
                    : "text-[#8A8A8A] hover:text-white hover:bg-white/5"
                }`}
                data-testid={`section-tab-${s.id}`}
              >
                {s.title}
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <div className="lg:col-span-9 space-y-6">
        {SECTIONS.filter((s) => s.id === activeId).map((s) => (
          <section
            key={s.id}
            className="border border-white/10 p-6 lg:p-8"
            data-testid={`section-${s.id}`}
          >
            <div className="flex items-center justify-between mb-6 gap-4">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-1">
                  Editing
                </div>
                <h3 className="font-display text-2xl uppercase font-bold tracking-tight text-white">
                  {s.title}
                </h3>
              </div>
              <button
                onClick={() => resetSection(s.id)}
                className="inline-flex items-center gap-2 border border-white/20 text-[#8A8A8A] hover:text-white hover:border-white px-3 py-2 text-[10px] tracking-[0.22em] uppercase transition-colors"
                data-testid={`reset-${s.id}`}
                title="Reset to defaults"
              >
                <RotateCcw size={12} /> Reset
              </button>
            </div>

            {s.type === "longtext" && (
              <textarea
                value={state.bio}
                onChange={(e) => setState({ ...state, bio: e.target.value })}
                rows={18}
                className="w-full bg-transparent border border-white/20 px-4 py-3 text-white text-sm leading-relaxed focus:border-[#E60000] font-sans"
                placeholder="Faith's personal bio…"
                data-testid="bio-textarea"
              />
            )}

            {s.type === "strings" && (
              <StringList
                values={state[s.id]}
                onChange={(arr) => setState({ ...state, [s.id]: arr })}
                placeholder={s.id === "tournaments" ? "Triple Crown NIT" : "AP World History"}
                testid={`strings-${s.id}`}
              />
            )}

            {s.type === "objects" && (
              <ObjectList
                items={state[s.id]}
                schema={SCHEMAS[s.id]}
                onChange={(arr) => setState({ ...state, [s.id]: arr })}
                testid={`objects-${s.id}`}
              />
            )}

            {s.type === "fields" && (
              <FieldGrid
                fields={s.fields}
                values={state}
                onChange={(k, v) => setState((prev) => ({ ...prev, [k]: v }))}
                testid={`fields-${s.id}`}
              />
            )}

            {s.type === "nested" && (
              <NestedGroups
                groups={s.groups}
                values={state}
                onChange={(k, v) => setState((prev) => ({ ...prev, [k]: v }))}
                testid={`nested-${s.id}`}
              />
            )}
          </section>
        ))}

        <div className="sticky bottom-4 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#FF3333] disabled:opacity-60 text-white px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold transition-colors shadow-2xl"
            data-testid="save-content"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Saving…" : "Save All Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StringList({ values, onChange, placeholder, testid }) {
  const set = (i, v) => onChange(values.map((x, idx) => (idx === i ? v : x)));
  const add = () => onChange([...values, ""]);
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid={testid}>
      {values.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A] w-6 shrink-0">
            {String(i + 1).padStart(2, "0")}
          </span>
          <input
            value={v}
            onChange={(e) => set(i, e.target.value)}
            placeholder={placeholder}
            className="flex-1 bg-transparent border border-white/20 px-3 py-2 text-white text-sm focus:border-[#E60000]"
          />
          <button
            onClick={() => remove(i)}
            className="text-[#8A8A8A] hover:text-[#E60000] p-2"
            title="Remove"
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        className="mt-2 inline-flex items-center gap-2 border border-dashed border-white/20 hover:border-[#E60000] hover:text-[#E60000] text-[#8A8A8A] px-4 py-2 text-[10px] tracking-[0.22em] uppercase transition-colors"
      >
        <Plus size={12} /> Add Item
      </button>
    </div>
  );
}

function ObjectList({ items, schema, onChange, testid }) {
  const set = (i, key, v) => onChange(items.map((o, idx) => (idx === i ? { ...o, [key]: v } : o)));
  const add = () => onChange([...items, Object.fromEntries(schema.map((f) => [f.key, ""]))]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4" data-testid={testid}>
      {items.map((o, i) => (
        <div key={i} className="border border-white/10 p-4 relative">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#E60000] font-bold">
              # {String(i + 1).padStart(2, "0")}
            </span>
            <button
              onClick={() => remove(i)}
              className="text-[#8A8A8A] hover:text-[#E60000] p-1"
              title="Remove"
            >
              <X size={14} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {schema.map((f) => (
              <label key={f.key} className="block sm:col-span-1">
                <span className="block text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A] mb-1 font-bold">
                  {f.label}
                </span>
                <input
                  value={o[f.key] || ""}
                  onChange={(e) => set(i, f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="w-full bg-transparent border border-white/20 px-3 py-2 text-white text-sm focus:border-[#E60000]"
                />
              </label>
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={add}
        className="inline-flex items-center gap-2 border border-dashed border-white/20 hover:border-[#E60000] hover:text-[#E60000] text-[#8A8A8A] px-4 py-2 text-[10px] tracking-[0.22em] uppercase transition-colors"
      >
        <Plus size={12} /> Add Item
      </button>
    </div>
  );
}

function cloneDefault(id) {
  const v = DEFAULTS[id];
  return Array.isArray(v) ? v.map((x) => (typeof x === "object" ? { ...x } : x)) : v;
}

function buildInitial(data) {
  const pick = (id) => (data[id] !== undefined ? cloneDeep(data[id]) : cloneDefault(id));
  const out = {
    bio: pick("bio") || "",
    apClasses: pick("apClasses") || [],
    extracurriculars: pick("extracurriculars") || [],
    tournaments: pick("tournaments") || [],
    awards: pick("awards") || [],
    upcoming: pick("upcoming") || [],
    press: pick("press") || [],
    profiles: pick("profiles") || [],
  };
  for (const k of SCALAR_KEYS) {
    out[k] = data[k] !== undefined && data[k] !== null ? String(data[k]) : (DEFAULTS[k] !== undefined ? String(DEFAULTS[k]) : "");
  }
  for (const k of COACH_KEYS) {
    out[k] = data[k] ? { ...data[k] } : { ...(DEFAULTS[k] || {}) };
  }
  return out;
}

function FieldGrid({ fields, values, onChange, testid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid={testid}>
      {fields.map((f) => (
        <label key={f.key} className="block">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A] mb-1 font-bold">
            {f.label}
          </span>
          <input
            value={values[f.key] ?? ""}
            onChange={(e) => onChange(f.key, e.target.value)}
            placeholder={f.placeholder}
            inputMode={f.numeric ? "numeric" : "text"}
            className="w-full bg-transparent border border-white/20 px-3 py-2 text-white text-sm focus:border-[#E60000]"
            data-testid={`field-${f.key}`}
          />
        </label>
      ))}
    </div>
  );
}

function NestedGroups({ groups, values, onChange, testid }) {
  return (
    <div className="space-y-6" data-testid={testid}>
      {groups.map((g) => {
        if (g.fields) {
          // nested object group
          const obj = values[g.key] || {};
          return (
            <div key={g.key} className="border border-white/10 p-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-4">
                {g.label}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {g.fields.map((f) => (
                  <label key={f.key} className="block">
                    <span className="block text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A] mb-1 font-bold">
                      {f.label}
                    </span>
                    <input
                      value={obj[f.key] ?? ""}
                      onChange={(e) => onChange(g.key, { ...obj, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      className="w-full bg-transparent border border-white/20 px-3 py-2 text-white text-sm focus:border-[#E60000]"
                      data-testid={`field-${g.key}-${f.key}`}
                    />
                  </label>
                ))}
              </div>
            </div>
          );
        }
        // scalar group
        return (
          <label key={g.key} className="block">
            <span className="block text-[10px] tracking-[0.25em] uppercase text-[#8A8A8A] mb-1 font-bold">
              {g.label}
            </span>
            <input
              value={values[g.key] ?? ""}
              onChange={(e) => onChange(g.key, e.target.value)}
              placeholder={g.placeholder}
              className="w-full bg-transparent border border-white/20 px-3 py-2 text-white text-sm focus:border-[#E60000]"
              data-testid={`field-${g.key}`}
            />
          </label>
        );
      })}
    </div>
  );
}

function cloneDeep(v) {
  if (Array.isArray(v)) return v.map(cloneDeep);
  if (v && typeof v === "object") return { ...v };
  return v;
}
